import { Environment, LeagueConfig } from '@leagueplatform/core';
import { BrowserRouter } from '@leagueplatform/routing';

import { ReactQueryDevtools } from 'react-query/devtools';
import React, { Suspense } from 'react';
import {
  initMasonryRegistry,
  ERROR_TYPES,
  ErrorState,
  FirebaseActivatorContainer,
  LoadingSpinner,
  useGetAuthConfig,
  useInjectDynatraceScript,
  QueryClientProvider,
  LeagueProvider,
  ErrorBoundary,
} from '@highmark-web/highmark-common';
import { AppRoutes } from 'routes/routes';
import { useOnlineStatus } from '@leagueplatform/web-common';
import { initializeNonConfigSDKs } from './config/init-non-config-sdks';
import { useGetLeagueConfig } from './config/league-config';
import { STANDALONE_AUTH_CONFIG } from './config/standalone-auth-config';
import { FIREBASE_ACTIVATE_CONFIG } from './common/firebase';
import { initAppMasonryRegistry } from './utils/init-app-masonry-registry';

initializeNonConfigSDKs();
initMasonryRegistry();
initAppMasonryRegistry();

export function App() {
  const { isOnline, isOffline } = useOnlineStatus();
  useInjectDynatraceScript(
    import.meta.env.VITE_DYNATRACE_APP_ID,
    import.meta.env.VITE_ENV as Environment,
  );

  const { isLoading: isAuthLoading, config: authConfig } = useGetAuthConfig(
    STANDALONE_AUTH_CONFIG,
  );
  const config: LeagueConfig = useGetLeagueConfig(authConfig);

  if (isAuthLoading) {
    return <LoadingSpinner />;
  }

  return (
    <BrowserRouter>
      <QueryClientProvider>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FirebaseActivatorContainer {...FIREBASE_ACTIVATE_CONFIG} />
        <LeagueProvider config={config}>
          <ErrorBoundary contextLocation="Root App ErrorBoundary">
            <Suspense fallback={<LoadingSpinner />}>
              {isOffline && (
                <ErrorState
                  error={null}
                  isFullPage
                  errorType={ERROR_TYPES.NO_CONNECTION}
                />
              )}
              {isOnline && <AppRoutes />}
            </Suspense>
            <ReactQueryDevtools />
          </ErrorBoundary>
        </LeagueProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
